// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, NavLink } from 'react-router-dom';
import { LoginPage } from './pages/Auth/LoginPage';
import { SignUpPage } from './pages/Auth/SignUpPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BuildingsProvider } from './contexts/BuildingsContext';
import HomePage from './pages/HomePage';
import LegacyDefaultPage from './pages/Legacy/LegacyDefaultPage';
import ProtectedRoute from './components/ProtectedRoute';
import { ForgotPasswordPage } from './pages/Auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/Auth/ResetPasswordPage';
import AdminRoute from './components/AdminRoute';
import OrganizationsPage from './pages/Admin/OrganizationsPage';
import BuildingsPage from './pages/Admin/BuildingsPage';
import UserManagementPage from './pages/Admin/UserManagementPage';
import ConnectorKeysPage from './pages/Admin/ConnectorKeysPage';
import { JoinOrganizationPage } from './pages/Auth/JoinOrganizationPage';

import { auth } from 'firebase-config';
import OrganizationDetailsPage from './pages/Admin/OrganizationDetailsPage';
import { Toaster } from 'components/ui/sonner';
import MyBuildingsPage from './pages/User/MyBuildingsPage';
import { Button } from 'components/ui/button';
import { LogOut, Building2, Shield, UserCheck, Home, Building, Users2, KeyRound, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';

function App() {
  return (
    <AuthProvider>
      <BuildingsProvider>
        <Router>
          <AppRoutes />
          <Toaster />
        </Router>
      </BuildingsProvider>
    </AuthProvider>
  );
}

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = window.location;

  // Check if we're on a reset password page
  const isResetPasswordPage = location.search.includes('oobCode');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <img 
              src="/logo512.png" 
              alt="ReCondo Logo" 
              className="w-16 h-16 object-contain"
            />
            <span className="text-3xl font-bold text-gray-900">ReCondo</span>
          </div>
          
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show reset password page if URL contains oobCode
  if (isResetPasswordPage) {
    return <ResetPasswordPage />;
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${
      isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    }`;

  return (
    <>
      {user ? (
        // Main App Layout for logged-in users
        <div className="min-h-screen w-full flex bg-muted/40">
          <aside className="fixed left-0 top-0 z-40 h-screen w-64 -translate-x-full border-r bg-background transition-transform sm:translate-x-0">
            <div className="flex h-full flex-col">
              <div className="flex h-14 items-center border-b px-4">
                <Link to="/" className="flex items-center gap-2">
                  <img 
                    src="/logo192.png" 
                    alt="ReCondo Logo" 
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-xl font-bold text-gray-900">ReCondo</span>
                </Link>
              </div>
              
              <nav className="flex-1 space-y-2 px-2 py-4">
                <div>
                  <div className="px-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dashboard</div>
                  <NavLink to="/" className={navLinkClass} end>
                    <Home className="h-4 w-4" />
                    Dashboard
                  </NavLink>
                </div>

                {user.role === 'admin' && (
                  <div className="pt-2">
                    <div className="px-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Admin</div>
                    <NavLink to="/admin/users" className={navLinkClass}>
                      <Users2 className="h-4 w-4" />
                      Users
                    </NavLink>
                    <NavLink to="/admin/organizations" className={navLinkClass}>
                      <Building className="h-4 w-4" />
                      Organizations
                    </NavLink>
                    <NavLink to="/admin/buildings" className={navLinkClass}>
                      <Building2 className="h-4 w-4" />
                      Buildings
                    </NavLink>
                    <NavLink to="/admin/connector-keys" className={navLinkClass}>
                      <KeyRound className="h-4 w-4" />
                      Connector Keys
                    </NavLink>
                  </div>
                )}
                
                <div className="pt-2">
                  <div className="px-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Riconciliazione</div>
                  <NavLink to="/legacy" className={navLinkClass}>
                    <Shield className="h-4 w-4" />
                    Avvia analisi bancaria IA
                  </NavLink>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 sm:ml-64">
            <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background px-4 sm:px-6">
              <div className="relative ml-auto flex-1 md:grow-0">
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="overflow-hidden rounded-full"
                  >
                    <UserCheck className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user.displayName || user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => auth.signOut()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </header>
            
            {/* Main Content Area */}
            <main className="flex-1 p-4 sm:px-6 sm:pt-2">
              <Routes>
                {/* Redirect auth pages if logged in */}
                <Route path="/login" element={<Navigate to="/" />} />
                <Route path="/signup" element={<Navigate to="/" />} />
                <Route path="/forgot-password" element={<Navigate to="/" />} />
                
                <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                <Route path="/join-organization" element={<ProtectedRoute><JoinOrganizationPage /></ProtectedRoute>} />
                
                {/* Admin Routes */}
                <Route path="/admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
                <Route path="/admin/organizations" element={<AdminRoute><OrganizationsPage /></AdminRoute>} />
                <Route path="/admin/organizations/:id" element={<AdminRoute><OrganizationDetailsPage /></AdminRoute>} />
                <Route path="/admin/buildings" element={<AdminRoute><BuildingsPage /></AdminRoute>} />
                <Route path="/admin/connector-keys" element={<AdminRoute><ConnectorKeysPage /></AdminRoute>} />
                
                {/* Public/User Routes */}
                <Route path="/legacy" element={<ProtectedRoute><LegacyDefaultPage /></ProtectedRoute>} />
                {/* Removed My Buildings from sidebar per request, route remains accessible if needed */}
                <Route path="/my-buildings" element={<ProtectedRoute><MyBuildingsPage /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </main>
          </div>
        </div>
      ) : (
        // Routes for logged-out users
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* Any other public routes can be added here */}
          
          {/* Redirect to login if trying to access a protected route */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      )}
    </>
  );
}

export default App;
